// Phase 7 — Editable bills table (used by Advance Settlement + Reimbursement).
// Rows: { sr_no, bill_type, attachment{filename,url,size,mime} }
// 3 rows default, hard cap 10.
// Phase 25a — bill attachment is now MANDATORY per filled row.
import { Paperclip, PlusCircle, X } from "lucide-react";

const BILL_MAX_MB = 10;
const BILL_EXTS = /\.(pdf|jpe?g|png|webp)$/i;
const BILL_MIMES = new Set([
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png", "image/webp",
]);

export default function BillsTable({ bills, setBills, api, toast, formatApiError,
                                     missingRows = [] }) {
  const missingSet = new Set(missingRows);
  const addRow = () => {
    if (bills.length >= 10) return;
    setBills([...bills, { sr_no: String(bills.length + 1), bill_type: "", attachment: null }]);
  };
  const rmRow = (i) => setBills(bills.filter((_, idx) => idx !== i));
  const setCell = (i, k, v) =>
    setBills(bills.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));

  const onUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!BILL_EXTS.test(file.name) || (file.type && !BILL_MIMES.has(file.type))) {
      toast.error("Bill attachment must be a PDF or image (pdf, jpg, jpeg, png, webp).");
      e.target.value = "";
      return;
    }
    if (file.size > BILL_MAX_MB * 1024 * 1024) {
      toast.error(`Bill attachment exceeds the ${BILL_MAX_MB} MB limit.`);
      e.target.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCell(i, "attachment", {
        field_name: `bill_${i}`,
        filename: data.filename, url: data.url, size: data.size,
        attachment_id: data.attachment_id || (data.url || "").split("/").pop(),
        mime: file.type || "application/octet-stream",
        kind: "bill_attachment",
      });
      toast.success(`${file.name} uploaded.`);
    } catch (err) {
      // Phase 25a — surface upload failures loudly and leave the slot
      // empty so the user can retry without a half-committed state.
      setCell(i, "attachment", null);
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="overflow-x-auto rounded-md border border-[#e2d7c3]">
        <table className="material-table w-full" data-testid="bills-table">
          <thead>
            <tr>
              <th className="w-24">S.No</th>
              <th>Bill Type</th>
              <th className="w-64">
                Bill Attachment <span className="text-[#b3261e]">*</span>
              </th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {bills.map((b, i) => {
              const missing = missingSet.has(i);
              return (
                <tr key={i}>
                  <td>
                    <input value={b.sr_no || ""}
                           onChange={(e) => setCell(i, "sr_no", e.target.value)}
                           data-testid={`bill-srno-${i}`} />
                  </td>
                  <td>
                    <input value={b.bill_type || ""}
                           placeholder="e.g. Taxi / Hotel / Meals"
                           onChange={(e) => setCell(i, "bill_type", e.target.value)}
                           data-testid={`bill-type-${i}`} />
                  </td>
                  <td>
                    <label
                      className={`inline-flex items-center gap-2 px-2 py-1 border border-dashed rounded-md bg-white cursor-pointer ${
                        missing
                          ? "border-[#b3261e] hover:border-[#b3261e]"
                          : "border-[#cbbba0] hover:border-[#7a1f2b]"
                      }`}
                      data-testid={`bill-attach-${i}`}
                    >
                      <Paperclip
                        size={13}
                        className={missing ? "text-[#b3261e]" : "text-[#7a1f2b]"}
                      />
                      <span className={`text-xs ${missing ? "text-[#b3261e]" : "text-[#4a524c]"}`}>
                        {b.attachment
                          ? b.attachment.filename
                          : "PDF/image · required · ≤10 MB"}
                      </span>
                      <input type="file" onChange={(e) => onUpload(i, e)}
                             accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
                             className="hidden" />
                    </label>
                  </td>
                  <td>
                    <button type="button" className="row-del"
                            onClick={() => rmRow(i)}
                            disabled={bills.length === 1}
                            aria-label="Remove row"
                            data-testid={`bill-remove-${i}`}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="btn-ghost" onClick={addRow}
                disabled={bills.length >= 10} data-testid="add-bill-row">
          <PlusCircle size={16} /> Add row
        </button>
        <span className="text-xs text-[#6e7770]">
          {bills.length} row(s) · max 10 · PDF or images only, up to 10 MB per file
        </span>
      </div>
      {missingRows.length > 0 && (
        <div
          className="mt-2 text-xs text-[#b3261e] font-medium"
          data-testid="bills-missing-attachment-error"
        >
          {missingRows.length === 1
            ? `Row ${missingRows[0] + 1}: Please attach a bill before submitting.`
            : `Rows ${missingRows.map((n) => n + 1).join(", ")}: Please attach a bill before submitting.`}
        </div>
      )}
    </div>
  );
}
